import agwaFilterSummaryStyles from './agwa-filter-summary.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import type {
  FilterClause,
  FilterFacet,
  FilterSelection,
} from './filter-facets';
import { describeFilterQuery } from './filter-facets';

/** Key used for the free-text clause, which is the host's search box rather than a facet. */
export const SEARCH_CLAUSE_KEY = 'search';

/**
 * `<agwa-filter-summary>` - the current query, stated as a sentence above the list.
 *
 * It exists because a rail of checkboxes can only show WHICH boxes are ticked. What it cannot show
 * is what they mean together: values inside one facet are OR'd with each other while separate
 * facets are AND'ed, and no arrangement of checkboxes makes that visible. Someone handed a list
 * narrowed by four conditions can read this sentence and know exactly why a company they expected
 * is missing.
 *
 * Each condition carries its own remove, so undoing one part of a query is a single click instead
 * of reopening the control that set it and hunting for the tick.
 *
 * The sentence keeps one line's height whether or not anything is filtered. It sits directly above
 * the rows, so a summary that appeared only once a filter was applied would push the whole list
 * down at the moment of filtering - the same defect the rail was built to remove. A query complex
 * enough to wrap to a second line does move the rows once, which is a deliberate limit rather than
 * an oversight: past three conditions, knowing what the query says is worth more than the row
 * holding still.
 *
 * Emits `filter-drop` - `{ key }`, the facet to clear, or `SEARCH_CLAUSE_KEY` for the search text.
 */
@customElement('agwa-filter-summary')
export class AgwaFilterSummary extends LitElement {
  static styles = unsafeCSS(agwaFilterSummaryStyles);

  @property({ type: Array }) facets: FilterFacet[] = [];

  @property({ type: Object }) selection: FilterSelection = {};

  /** The host's free-text search, rendered as a final clause. Empty means no text clause. */
  @property({ type: String, attribute: 'search-text' }) searchText = '';

  /**
   * What is being listed, plural and already resolved from the glossary by the host - the sentence
   * must never hard-code a domain word.
   */
  @property({ type: String }) subject = 'records';

  /** How many rows survive the query. */
  @property({ type: Number, attribute: 'shown-count' }) shownCount = 0;

  /**
   * How many records there are before filtering, or null when the host cannot say. A list
   * that is paged on the server only learns how many rows match its query, never how many it
   * would show unfiltered without a second query it has no other use for - so the sentence
   * says "Showing 12 products that are Active" and leaves "of N" out rather than inventing N.
   */
  @property({ type: Number, attribute: 'total-count' }) totalCount:
    number | null = 0;

  /** Facet clauses plus the search clause, in the order the sentence reads them. */
  private get clauses(): FilterClause[] {
    const clauses = describeFilterQuery(this.facets, this.selection);
    if (this.searchText) {
      clauses.push({
        key: SEARCH_CLAUSE_KEY,
        phrase: 'matching',
        labels: [`“${this.searchText}”`],
      });
    }
    return clauses;
  }

  private drop(key: string) {
    this.dispatchEvent(
      new CustomEvent('filter-drop', {
        detail: { key },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * The words joining one clause to the next. Commas throughout with "and" before the last is
   * what makes this read as a sentence rather than a list of applied filters, which is the whole
   * reason it is here.
   */
  private separator(index: number, total: number): string {
    if (index === 0) return ' ';
    // Two conditions take a bare "and"; three or more take the comma before it. "Active, and
    // operating as Glass Processor" reads as a list that lost an item.
    if (index === total - 1) return total === 2 ? ' and ' : ', and ';
    return ', ';
  }

  private renderClause(clause: FilterClause) {
    return html`<span class="clause"
      >${clause.phrase}
      ${clause.labels.map(
        (label, i) =>
          html`${i > 0 ? html`<span class="or"> or </span>` : null}<b
              >${label}</b
            >`
      )}<sl-icon-button
        name="x"
        label="Stop filtering by ${clause.phrase}"
        @click=${() => this.drop(clause.key)}
      ></sl-icon-button
    ></span>`;
  }

  render() {
    const clauses = this.clauses;

    // Nothing applied still occupies the line, and says what the list is rather than going blank.
    if (clauses.length === 0) {
      // With nothing applied, shown and total are the same number, so the host's own count
      // stands in when it cannot say what the unfiltered total is.
      return html`<p class="summary">
        Showing all
        <b>${this.totalCount ?? this.shownCount}</b> ${this.subject}.
      </p>`;
    }

    return html`<p class="summary">
      Showing <b>${this.shownCount}</b>${
        this.totalCount === null ? '' : html` of <b>${this.totalCount}</b>`
      }
      ${this.subject}${clauses.map(
        (clause, index) =>
          html`${this.separator(index, clauses.length)}${this.renderClause(
            clause
          )}`
      )}.
    </p>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-filter-summary': AgwaFilterSummary;
  }
}
